// Auto-generated from docs/architecture/digital-twin.md — do not edit manually
const content = `
# Digital Twin Architecture

The Digital Twin provides real-time simulation visualization for Vortx Flux sites. It runs a headless simulation engine on a GCE VM that ticks through a full-year (35,040 timestep) scenario at configurable speed, streaming state snapshots over WebSocket to a 3D browser frontend.

## Overview

The Digital Twin simulates a site's energy assets (solar, battery, EV fleet, cold storage) operating against real load and tariff data from optimized scenarios. Users configure which app scenarios to include, set a simulation speed (1x real-time to 1000x), and watch the site operate in a 3D viewport with live metrics, energy flow visualization, and the ability to inject abnormality events (cloud cover, load spikes, equipment trips).

Two simulation modes are available:

- **Real-Time Simulation** -- 3D viewport with VM-backed tick loop, WebSocket streaming, and interactive controls.
- **Annual Simulation** -- Batch simulation via the existing EMS orchestrator (separate from the Digital Twin infrastructure).

## Architecture

\`\`\`
Browser (flux-ems-web)
    |
    | HTTPS POST (start simulation)
    v
Cloud Function (startSimulationVM)
    |
    | GCE API: start/create VM
    | HTTP POST to VM /start endpoint
    | Firestore: write twin_simulations doc
    v
GCE VM (vortx-sim-server, e2-small, us-central1-a)
    |
    | ws://VM_IP:8765/ws (plain WebSocket)
    v
Cloud Run (sim-ws-proxy)
    |
    | wss://sim-ws-proxy-xxx.run.app/ws?vm_ip=<IP>
    | (TLS-terminated, bidirectional proxy)
    v
Browser (WebSocket client)
\`\`\`

The browser never connects directly to the VM. All WebSocket traffic routes through the \`sim-ws-proxy\` Cloud Run service, which provides WSS (TLS termination) required by HTTPS-served pages. The Cloud Function handles VM lifecycle (create, start, health-check, start simulation) and writes metadata to Firestore.

### Why This Architecture

- **GCE VM (not Cloud Run)**: The simulation is a long-running stateful process that ticks continuously for hours or days. Cloud Run's request-timeout model does not suit persistent tick loops.
- **Cloud Run proxy**: The frontend is served over HTTPS, which requires WSS for WebSocket connections. The GCE VM only exposes plain \`ws://\`. The proxy bridges this gap with minimal latency.
- **Cloud Function orchestrator**: The \`startSimulationVM\` function encapsulates the multi-step boot sequence (start VM, wait for health, POST /start, write Firestore) into a single HTTP call from the frontend.

## Sim Server

**App**: \`apps/vortx-sim-server/\`
**Runtime**: Python 3.11 (FastAPI + Uvicorn), Docker container on GCE
**Port**: 8765

### Tick Loop (\`simulation_manager.py\`)

The \`SimulationManager\` class runs a headless simulation loop as an asyncio task. It is completely independent of frontend viewers -- it runs whether or not any WebSocket clients are connected.

Key constants:

| Constant | Value | Description |
|----------|-------|-------------|
| \`TIMESTEP_HOURS\` | 0.25 | 15-minute intervals |
| \`STEPS_PER_DAY\` | 96 | Quarter-hourly steps per day |
| \`STEPS_PER_YEAR\` | 35,040 | Full year of quarter-hourly steps |
| \`REAL_SECONDS_PER_STEP\` | 900 | 15 minutes in real seconds |
| \`SIM_START\` | 2026-01-01 00:00 | Simulation epoch |

The main loop (\`SimulationManager.run()\`) executes this cycle on every tick:

1. **\`_simulate_tick()\`** -- Runs simplified agent heuristics for battery (price-based arbitrage), EV fleet (schedule-based charging), and cold storage (duty cycle). Applies event modifiers from \`EventInjector\`. Calculates grid balance, peaks, and costs. Builds equipment state dict and energy flow list.
2. **\`build_state_snapshot()\`** -- Assembles the full state snapshot with tick metadata, equipment states, flows, metrics, and active events.
3. **\`streamer.broadcast(state)\`** -- Sends the snapshot to all connected WebSocket clients.
4. **Checkpoint** -- Every 96 ticks (1 simulated day), saves state to Firestore via \`StateCheckpointer\`.
5. **Day/month boundary resets** -- Resets daily metrics, monthly peak tracking.
6. **Pacing** -- Sleeps for \`REAL_SECONDS_PER_STEP / speed\` seconds. At 1x, each tick takes 15 real minutes. At 1000x, ticks run as fast as possible (capped at 1ms sleep).

The simulation supports:

- **Pause/resume** -- \`sim_manager.paused = True/False\`
- **Step-through** -- \`step_once()\` advances exactly one timestep when paused
- **Speed changes** -- \`sim_manager.speed = N\` adjusts pacing mid-run
- **Event injection** -- \`inject_event(event)\` modifies simulation conditions for a duration
- **Checkpoint restore** -- \`restore_from_checkpoint(checkpoint)\` resumes from a Firestore checkpoint after VM restart

### V1 Agent Heuristics

The current implementation uses simplified agent logic (noted as V1 in the code, to be replaced with real \`RollingOrchestrator\` agents):

- **Battery**: Charges when energy price < 0.8x average, discharges when price > 1.2x average. Respects SOC bounds (0.1--0.9) and site capacity limits.
- **EV Fleet**: Vehicles present overnight (18:00--06:00). 60% of present vehicles charge at 7.2 kW (Level 2). SOC decreases during driving hours.
- **Cold Storage**: Duty cycle -- compressor on for 3 of every 4 quarter-hours. Power from config.

### HTTP Endpoints (\`app.py\`)

| Endpoint | Method | Description |
|----------|--------|-------------|
| \`/health\` | GET | Health check (returns \`{"status": "ok"}\`) |
| \`/status\` | GET | Current simulation status (tick, sim_time, speed, paused) |
| \`/start\` | POST | Start a new simulation. Accepts \`StartConfig\` with project_id, scenario_ids, user_id, sim_id, speed. Loads config via \`ConfigLoader\`, creates \`SimulationManager\`, launches as asyncio task. |
| \`/stop\` | POST | Stop simulation. Saves final checkpoint to Firestore, schedules VM shutdown via GCE metadata API after 5-second delay. |
| \`/ws\` | WebSocket | Bidirectional WebSocket. Server pushes state snapshots; client sends commands (set_speed, pause, resume, step, inject_event, stop). |

### VM Self-Stop

When a simulation ends (via \`/stop\` or natural completion), the server:

1. Saves a final checkpoint to Firestore
2. Updates \`twin_simulations\` doc status to "stopped"
3. Discovers its own instance name and zone via the GCE metadata server
4. Calls \`gcloud compute instances stop\` in a background subprocess

This ensures the VM does not accrue costs when idle.

### State Streamer (\`state_streamer.py\`)

\`StateStreamer\` manages connected WebSocket clients and broadcasts state to all of them. Dead clients are automatically removed on send failure. Messages use JSON format:

\`\`\`json
{ "type": "state", "data": { ... state snapshot ... } }
\`\`\`

Non-state events use the same format with a different \`type\` field (e.g., \`"simulation_complete"\`).

### Event Injector (\`event_injector.py\`)

\`EventInjector\` manages active abnormality events that modify simulation conditions. Each event has a type, duration in steps, and type-specific parameters. Events are ticked down each simulation step and automatically removed when expired.

Supported event types:

| Type | Effect | Key Parameters |
|------|--------|----------------|
| \`cloud_cover\` | Multiplies PV generation by \`(1 - severity)\` | \`severity\` (0--1) |
| \`load_spike\` | Adds kW to building load | \`magnitude_kw\` |
| \`price_spike\` | Multiplies energy prices | \`multiplier\` |
| \`temp_extreme\` | Overrides ambient temperature | \`temp_f\` |
| \`equipment_trip\` | Takes equipment offline | \`equipment\` (solar, battery, ev_fleet, cold_storage) |

### State Checkpointer (\`state_checkpointer.py\`)

\`StateCheckpointer\` saves simulation state to Firestore every 96 ticks (1 simulated day) for resume capability after VM restarts.

Firestore paths:

- Checkpoint subcollection: \`users/{uid}/projects/{pid}/twin_simulations/{sim_id}/checkpoints/{day}\`
- Latest checkpoint (quick access): \`users/{uid}/projects/{pid}/twin_simulations/{sim_id}\` \`.latest_checkpoint\` field

Checkpoint data includes tick, day, sim_time, speed, equipment states, metrics, and active events.

## Config Loader (\`config_loader.py\`)

\`ConfigLoader\` reads project and scenario data from Firestore and Cloud Storage to build the configuration dict that \`SimulationManager\` expects.

### Data Sources

- **Project doc**: \`users/{uid}/projects/{pid}\` -- provides \`site_capacity_kw\`
- **Scenario doc**: \`users/{uid}/projects/{pid}/scenarios/{sid}\` -- provides \`storage_path\` to Cloud Storage
- **Scenario JSON** (Cloud Storage): Full REopt-format scenario with inputs and outputs

### Field Mapping (REopt Format)

| Sim Config Field | Source |
|------------------|--------|
| \`site_capacity_kw\` | Project document |
| \`profiles.load_kw\` | \`reopt_inputs.ElectricLoad.loads_kw\` |
| \`profiles.prices\` | \`outputs.ElectricTariff.energy_rate_average_series\` |
| \`profiles.pv_kw\` | \`outputs.PV.electric_to_load_series_kw\` |
| \`demand_charge_rate\` | \`outputs.ElectricTariff.demand_rate_average_series[0]\` |
| \`agents.battery\` | \`outputs.ElectricStorage.size_kw / size_kwh\` |

### Multi-App Mode (\`load_multi\`)

The Digital Twin supports merging scenarios from multiple apps into a single simulation:

- **Site DNA** (required): Provides the base load profile, energy prices, solar production, battery config, and site capacity.
- **Flux EV** (optional): Adds EV fleet config (vehicle count, charger kW, battery kWh) from \`ev_scenarios\` subcollection.
- **Flux COLD** (optional): Adds cold storage config (compressor power) from \`cold_scenarios\` subcollection.

If Site DNA is not provided, the loader raises a \`ValueError\`. All profiles are normalized to 35,040 quarter-hourly values -- 8,760-length hourly arrays are upsampled 4x.

## WebSocket Proxy (\`apps/sim-ws-proxy/\`)

A lightweight FastAPI service running on Cloud Run that proxies WebSocket connections between the browser (WSS) and the sim server VM (plain WS).

### How It Works

1. Browser connects to \`wss://sim-ws-proxy-xxx.run.app/ws?vm_ip=<IP>\`
2. Proxy validates that \`vm_ip\` belongs to the known sim server VM by querying the GCE API for \`vortx-sim-server\`'s external IP
3. Proxy opens a \`ws://<vm_ip>:8765/ws\` connection to the VM
4. Two concurrent asyncio tasks forward messages bidirectionally:
   - \`client_to_vm()\`: Browser messages forwarded to VM
   - \`vm_to_client()\`: VM messages forwarded to browser
5. When either direction disconnects, the other is cancelled

### Security

- The proxy validates the \`vm_ip\` query parameter against the actual IP addresses assigned to the \`vortx-sim-server\` GCE instance. If the IP does not match, the connection is rejected with code 4003.
- The VM's WebSocket port (8765) is protected by a GCE firewall rule (\`allow-sim-ws\`) restricted to the \`sim-server\` network tag.

### Configuration

| Constant | Value |
|----------|-------|
| \`GCP_PROJECT_ID\` | \`vortx-flux-site-dna-389f0\` |
| \`VM_NAME\` | \`vortx-sim-server\` |
| \`ZONE\` | \`us-central1-a\` |
| \`SIM_PORT\` | 8765 |

## GCE VM Management

### Cloud Function: \`startSimulationVM\`

**Path**: \`functions/startSimulationVM/index.js\`
**Trigger**: HTTP POST
**Region**: us-central1
**Timeout**: 240 seconds
**Memory**: 512 MiB

The Cloud Function orchestrates the full simulation startup sequence:

1. **Validate request** -- Requires \`project_id\`, \`scenario_id\` or \`scenario_ids\`, \`user_id\`, \`config.speed\`, \`config.duration_days\`. No fallback defaults.
2. **Check VM state**:
   - **RUNNING**: Reuse existing VM, get its IP
   - **TERMINATED/STOPPED**: Start the VM, wait for external IP (exponential backoff, 60s max)
   - **Does not exist**: Create new VM (e2-small, Debian 12, sim-server network tag), wait for IP
   - **Transitional state** (STAGING, STOPPING): Error -- user must retry
3. **Write Firestore metadata** -- Creates \`twin_simulations/{sim_id}\` doc with status "booting", VM IP, scenario IDs, config
4. **Wait for health** -- Polls \`http://<vm_ip>:8765/health\` with exponential backoff (90s max)
5. **Start simulation** -- Stops any lingering simulation, then POST \`/start\` with the full payload (retries once on 409)
6. **Return** -- \`{ vm_ip, sim_id, status: "running", vm_action }\` to the frontend

### VM Startup Script (\`startup.sh\`)

When the GCE VM boots, it runs \`startup.sh\` which:

1. Pulls the latest Docker image from \`gcr.io/vortx-flux-site-dna-389f0/vortx-sim-server:latest\`
2. Stops and removes any existing container
3. Runs the container on port 8765 with the \`GOOGLE_CLOUD_PROJECT\` environment variable

### Infrastructure Setup (\`tools/setup-sim-server.sh\`)

One-time setup script that creates:

1. GCE VM instance (e2-small, created stopped)
2. Firewall rule \`allow-sim-ws\` (tcp:8765, target tag: sim-server)
3. Static IP reservation (\`sim-server-ip\`)

### Deployment (\`tools/deploy-sim-server.sh\`)

Builds and pushes the Docker image to GCR:

1. Copies Firebase Admin credentials into the build context
2. Bundles \`flux_core\` library from \`libs/python/flux-core/\`
3. Submits Cloud Build to build and tag the image
4. Cleans up bundled files

The VM pulls the new image on its next startup (via the startup script).

## Frontend Components

All Digital Twin frontend components live in \`apps/flux-ems-web/src/components/twin/\`.

### DigitalTwinTab (\`DigitalTwinTab.jsx\`)

Top-level component. Manages:

- **Mode selection** between Real-Time and Annual simulation
- **Simulation lifecycle**: boot VM via Cloud Function, connect WebSocket, stop
- **WebSocket URL construction**: \`wss://sim-ws-proxy-xxx.run.app/ws?vm_ip=<IP>\`
- **State management**: sim state from WebSocket, equipment history for sparklines, terrain data accumulation
- **3D viewport**: renders \`SiteScene\` with equipment meshes, floating labels, energy flow conduits
- **Overlays**: \`EquipmentDetailPanel\` (click on equipment), \`LoadProfileTerrain\` (live terrain toggle)

### SimulationSetup (\`SimulationSetup.jsx\`)

Pre-start configuration screen with:

- Per-app scenario selectors (Site DNA, Flux EV, Flux COLD) with dropdowns populated from the \`useTwinScenarios\` hook
- Duration selection (1 Week, 1 Month, 1 Year)
- Speed presets (Real-time 1x, Fast 10x, Turbo 100x)
- Recent simulation runs table with status, progress, and kill button

### SimulationControls (\`SimulationControls.jsx\`)

Bottom control bar during a running simulation:

- Play/pause toggle (hidden at real-time speed since you cannot pause real-time)
- Step-forward button (only when paused in accelerated mode)
- Speed slider with logarithmic scale (0.5x to 1000x)
- Simulation time display (formatted date + day counter)
- Stop button with confirmation dialog

### EquipmentDetailPanel (\`EquipmentDetailPanel.jsx\`)

Absolute-positioned overlay showing live equipment metrics and a mini sparkline chart for the primary metric. Appears when an equipment mesh is clicked in the 3D viewport. Metric definitions come from \`equipmentDefs.js\`.

### SiteScene (\`SiteScene.jsx\`)

Three.js Canvas container (via \`@react-three/fiber\`) providing:

- Perspective camera positioned above/behind the site
- OrbitControls (rotate, zoom, pan with angle limits)

---
*This page is auto-generated from \`docs/architecture/\`. See the full document for complete details.*
`;

export default content;
